interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: 'website' | 'article';
  lastModified?: string;
  schema?: Record<string, unknown> | Record<string, unknown>[];
}

/**
 * SEOHead — React 19 document metadata hoisting
 * Handles: title, meta, Open Graph, Twitter Card, geo tags, JSON-LD structured data
 */
export default function SEOHead({
  title,
  description,
  keywords,
  ogTitle,
  ogDescription,
  ogImage = 'https://readdy.ai/api/search-image?query=professional%20child%20neuropsychological%20evaluation%20office%20warm%20modern%20teal%20branding&width=1200&height=630&seq=og-default&orientation=landscape',
  ogType = 'website',
  lastModified,
  schema,
}: SEOHeadProps) {
  const canonical =
    typeof window !== 'undefined'
      ? `${window.location.origin}${window.location.pathname}`
      : '';

  const schemaArray = schema
    ? Array.isArray(schema)
      ? schema
      : [schema]
    : [];

  const schemaJson = JSON.stringify(
    schemaArray.length === 1 ? schemaArray[0] : schemaArray,
  );

  return (
    <>
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      {canonical && <link rel="canonical" href={canonical} />}
      {lastModified && <meta name="last-modified" content={lastModified} />}

      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={ogTitle ?? title} />
      <meta property="og:description" content={ogDescription ?? description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content="pt_BR" />
      <meta property="og:site_name" content="Neuro Elos" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={ogTitle ?? title} />
      <meta name="twitter:description" content={ogDescription ?? description} />
      <meta name="twitter:image" content={ogImage} />

      {/* Geo Tags — São Paulo, SP, Brasil */}
      <meta name="geo.region" content="BR-SP" />
      <meta name="geo.placename" content="São Paulo, SP, Brasil" />
      <meta name="geo.position" content="-23.5505;-46.6333" />
      <meta name="ICBM" content="-23.5505, -46.6333" />

      {/* Robots */}
      <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />

      {/* JSON-LD Structured Data */}
      {schemaArray.length > 0 && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: schemaJson }}
        />
      )}
    </>
  );
}
